import { apiClient } from "./client";
import {
    ONLINE_STORE_LABEL,
    type RetailPromotionArticlesResponse,
    type RetailPromotionHistoryImportPayload,
    type RetailPromotionHistoryResponse,
    type RetailPromotionsResponse,
} from "./onlineCockpitTypes";

function periodStoreParams(params: {
    period?: string;
    dateFrom?: string;
    dateTo?: string;
    store?: string;
}) {
    return {
        period: params.period,
        date_from: params.dateFrom,
        date_to: params.dateTo,
        store: params.store ?? ONLINE_STORE_LABEL,
    };
}

export async function fetchOnlinePromotionsReport(params: {
    period?: string;
    dateFrom?: string;
    dateTo?: string;
    store?: string;
    baseDateFrom?: string;
    baseDateTo?: string;
}) {
    const { data } = await apiClient.get<RetailPromotionsResponse>("/reports/online-promotions", {
        params: {
            ...periodStoreParams(params),
            base_date_from: params.baseDateFrom || undefined,
            base_date_to: params.baseDateTo || undefined,
        },
    });

    return data;
}

export async function fetchOnlinePromotionArticles(
    ruleId: number,
    params: {
        period?: string;
        dateFrom?: string;
        dateTo?: string;
        store?: string;
        search?: string;
        page?: number;
        perPage?: number;
        soldOnly?: boolean;
    },
) {
    const { data } = await apiClient.get<RetailPromotionArticlesResponse>(
        `/reports/online-promotions/${ruleId}/articles`,
        {
            params: {
                ...periodStoreParams(params),
                search: params.search,
                page: params.page,
                per_page: params.perPage,
                sold_only: params.soldOnly ? 1 : undefined,
            },
        },
    );

    return data;
}

export async function fetchOnlinePromotionHistoryArticles(
    ruleId: number,
    params: {
        store?: string;
        search?: string;
        page?: number;
        perPage?: number;
        soldOnly?: boolean;
    },
) {
    const { data } = await apiClient.get<RetailPromotionArticlesResponse>(
        `/reports/online-promotions/history/${ruleId}/articles`,
        {
            params: {
                store: params.store ?? ONLINE_STORE_LABEL,
                search: params.search,
                page: params.page,
                per_page: params.perPage,
                sold_only: params.soldOnly ? 1 : undefined,
            },
        },
    );

    return data;
}

export async function updateRetailPromotionMarketingCost(ruleId: number, marketingCost: number) {
    const { data } = await apiClient.put<{ ruleId: number; marketingCost: number }>(
        `/reports/retail-promotions/${ruleId}/marketing-cost`,
        { marketing_cost: marketingCost },
    );

    return data;
}

export async function fetchOnlinePromotionHistory(params?: {
    store?: string;
    dateFrom?: string;
    dateTo?: string;
}) {
    const { data } = await apiClient.get<RetailPromotionHistoryResponse>(
        "/reports/online-promotions/history",
        {
            params: {
                store: params?.store ?? ONLINE_STORE_LABEL,
                date_from: params?.dateFrom,
                date_to: params?.dateTo,
            },
        },
    );

    return data;
}

export async function fetchRetailPromotionHistory(params?: {
    store?: string;
    dateFrom?: string;
    dateTo?: string;
}) {
    return fetchOnlinePromotionHistory(params);
}

export async function fetchRetailPromotionArticles(
    ruleId: number,
    params: Parameters<typeof fetchOnlinePromotionArticles>[1],
) {
    return fetchOnlinePromotionArticles(ruleId, params);
}

export async function fetchRetailPromotionHistoryArticles(
    ruleId: number,
    params: Parameters<typeof fetchOnlinePromotionHistoryArticles>[1],
) {
    return fetchOnlinePromotionHistoryArticles(ruleId, params);
}

export async function importRetailPromotionHistory(payload: RetailPromotionHistoryImportPayload) {
    const { data } = await apiClient.post<{
        ruleId: number;
        insertedHistory: boolean;
        insertedArticles: number;
        message: string;
    }>("/reports/retail-promotions/history/import", {
        store_name: payload.storeName || ONLINE_STORE_LABEL,
        name: payload.name,
        promo_from: payload.promoFrom,
        promo_to: payload.promoTo,
        discount_percent: payload.discountPercent ?? null,
        replace_existing: payload.replaceExisting ?? false,
        articles: payload.articles.map((row) => ({
            article_code: row.articleCode,
            article_name: row.articleName,
            group_name: row.groupName ?? null,
            regular_price: row.regularPrice,
            promo_price: row.promoPrice,
            discount_percent: row.discountPercent ?? null,
        })),
    });

    return data;
}
